/**
 * Ghosts — a student's own mistakes, given a shape (`docs/rpg.md` §18, §21).
 *
 * The encounter's third beat asks a student to *name* what went wrong rather
 * than to compute another answer, and this file is where that question comes
 * from. It measures nothing and teaches nothing new; like `journal.ts`, it
 * joins up two things the platform already has.
 *
 * The first is the misconception catalog. `packages/exercises/src/misconceptions.ts`
 * holds 550 named errors, each with a blameless `description` of what the
 * student is (understandably) doing, a second-person `studentMessage` for the
 * moment of failure, and the concept that repairs it. Somebody already wrote
 * the bestiary; it just was not shaped like one.
 *
 * The second is the world. Every misconception names a `remediationConceptId`,
 * every available concept belongs to exactly one region of exactly one kingdom
 * (`validateWorld` enforces it), and so every misconception has a *home*.
 *
 * Four rules shape everything below.
 *
 * **A ghost is a ward, not an error.** It is keyed by the concept that repairs
 * it — the thing to go back and learn — and the misconceptions remediated by
 * that concept are its *tricks*. One creature with two or three tricks reads
 * like a creature; 550 of them reads like a spreadsheet, and would ask content
 * to name every one.
 *
 * **The name is the mathematics.** `The ${form} of ${concept.title}` — the form
 * is costume, drawn from a small authored cast, and the noun is the
 * curriculum's own title. Nothing here invents a word for a piece of
 * mathematics, which is what keeps a bestiary of 244 ghosts free of authoring
 * and free of ghosts whose names quietly disagree with the concept page.
 *
 * **A ghost recurs, because the error does.** The Wraith of Common Denominators
 * haunts a Grade 4 student adding fractions and the same Wraith turns up in
 * Grade 9 rational expressions, because it is the same mistake. Keying the
 * bestiary off the ward rather than off the kingdom you happened to meet it in
 * is what makes that true rather than a nice line in a design document.
 *
 * **No clocks, no randomness** (`CLAUDE.md`). A seal's choices and their order
 * are derived from a seed, so a reloaded page offers the same four statements
 * in the same order — the reload bug `encounter.ts` exists to avoid, one beat
 * later.
 */

import type { CurriculumRegistry } from '@yuan-math/curriculum';
import type { Misconception } from '@yuan-math/exercises';
import type { AttemptRecord } from '@yuan-math/learning';
import { hashString } from '@yuan-math/math';
import { conceptKingdoms } from './state.ts';
import type { Kingdom } from './types.ts';

/* -------------------------------------------------------------------------- */
/* The cast                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * What haunts one kingdom. Authored in `content/src/world/ghosts.ts`, one per
 * kingdom, and the whole of what a human writes for this feature.
 *
 * `forms` are nouns that read as `The ${form} of ${title}` — "The Wraith of
 * Common Denominators". Which form a ghost takes is derived from its ward, so a
 * ghost keeps its name for ever without anyone recording that it has one.
 */
export type GhostArchetype = {
  kingdomId: string;
  emoji: string;
  /** Two or more, distinct within the archetype. */
  forms: string[];
  /** One sentence of place, for the bestiary page. Written for a student. */
  habitat: string;
};

/**
 * One creature: a concept that repairs something, wearing a kingdom's costume.
 *
 * Derived rather than authored, and derived rather than stored — the whole
 * bestiary is a pure function of the catalog, the curriculum and the world, so
 * there is nothing here to fall out of step with any of the three.
 */
export type Ghost = {
  /** `ghost:<wardConceptId>`. Stable across sessions and across content edits. */
  id: string;
  /** "The Wraith of Common Denominators". */
  name: string;
  emoji: string;
  kingdomId: string;
  /** The concept that wards it: what to go back and learn. */
  wardConceptId: string;
  /** Its title, read off the curriculum rather than repeated here. */
  wardTitle: string;
  /** The misconceptions it knows, in catalog order. Never empty. */
  trickIds: string[];
};

/** The id a ghost is known by. One per ward concept, for ever. */
export const ghostId = (wardConceptId: string): string => `ghost:${wardConceptId}`;

/**
 * Everything the bestiary reads. A parameter object for the reason
 * `EncounterRun` is one: `content/` sits above this package, so the catalog,
 * the curriculum and the world all arrive from the caller.
 */
export type GhostWorld = {
  archetypes: readonly GhostArchetype[];
  kingdoms: readonly Kingdom[];
  registry: CurriculumRegistry;
  misconceptions: readonly Misconception[];
};

/**
 * The bestiary, and the two ways anything ever asks for a ghost.
 *
 * Built in one pass and handed back whole, because a page showing a fight wants
 * to look a ghost up per answer and rebuilding 550 entries per keystroke is the
 * kind of thing that is invisible until it is not. Callers memoise this;
 * nothing here is stateful.
 */
export type GhostIndex = {
  /** Every ghost, by kingdom order then ward title. */
  all: Ghost[];
  /** By the concept that wards it. */
  byWard: Map<string, Ghost>;
  /** By any of its tricks — the lookup a wrong answer does. */
  byTrick: Map<string, Ghost>;
  /** The catalog, by id, so a seal need not scan it. */
  misconceptionById: Map<string, Misconception>;
};

/**
 * Builds the bestiary.
 *
 * A misconception whose remediation concept belongs to no kingdom is skipped
 * rather than guessed at — it would be a ghost with no home, and inventing one
 * would put a creature on a map that the map does not contain.
 * `validateGhosts` fails the build on exactly that case, so the skip here is
 * belt to its braces and never the thing that hides it.
 */
export function ghostIndex(world: GhostWorld): GhostIndex {
  const kingdomOfConcept = conceptKingdoms(world.kingdoms);
  const archetypeOfKingdom = new Map(world.archetypes.map((a) => [a.kingdomId, a]));
  const order = new Map(world.kingdoms.map((kingdom, index) => [kingdom.id, index]));

  /* Group the catalog by ward first: a ghost is a ward, and its tricks are
     whatever the catalog remediates with that concept. */
  const tricksByWard = new Map<string, string[]>();
  for (const misconception of world.misconceptions) {
    const ward = misconception.remediationConceptId;
    tricksByWard.set(ward, [...(tricksByWard.get(ward) ?? []), misconception.id]);
  }

  const byWard = new Map<string, Ghost>();
  const byTrick = new Map<string, Ghost>();

  for (const [wardConceptId, trickIds] of tricksByWard) {
    const kingdomId = kingdomOfConcept.get(wardConceptId);
    const archetype = kingdomId ? archetypeOfKingdom.get(kingdomId) : undefined;
    const concept = world.registry.concept(wardConceptId);
    if (!kingdomId || !archetype || !concept || archetype.forms.length === 0) continue;

    /* Derived from the ward, so the ghost's name is a fact about the
       mathematics and not a stored decision that could drift. */
    const form = archetype.forms[hashString(wardConceptId) % archetype.forms.length] as string;
    const ghost: Ghost = {
      id: ghostId(wardConceptId),
      name: `The ${form} of ${concept.title}`,
      emoji: archetype.emoji,
      kingdomId,
      wardConceptId,
      wardTitle: concept.title,
      trickIds,
    };

    byWard.set(wardConceptId, ghost);
    for (const trickId of trickIds) byTrick.set(trickId, ghost);
  }

  const all = [...byWard.values()].sort(
    (a, b) =>
      (order.get(a.kingdomId) ?? 0) - (order.get(b.kingdomId) ?? 0) ||
      a.wardTitle.localeCompare(b.wardTitle),
  );

  return {
    all,
    byWard,
    byTrick,
    misconceptionById: new Map(world.misconceptions.map((m) => [m.id, m])),
  };
}

/** The ghost that plays this trick, or none if the catalog entry has no home. */
export function ghostForTrick(index: GhostIndex, misconceptionId: string): Ghost | undefined {
  return index.byTrick.get(misconceptionId);
}

/** Everything that haunts one kingdom, in bestiary order. */
export function ghostsOfKingdom(index: GhostIndex, kingdomId: string): Ghost[] {
  return index.all.filter((ghost) => ghost.kingdomId === kingdomId);
}

/**
 * The catalog entry behind one trick — its short `title`, its blameless
 * third-person `description`, and the second-person `studentMessage` written
 * for the moment a student takes it.
 *
 * Returned whole rather than as three accessors, because which of them a
 * screen wants is the screen's business and it differs. The encounter's fight
 * shows the `title` beside the ghost's name and lets the ordinary exercise
 * runner give the `studentMessage`, because both on one screen is the same
 * sentence twice; the seal shows the `description`.
 *
 * Whatever is shown is passed through rather than rewritten, and that is the
 * point. These lines were written by somebody who thought about how they land,
 * and a fantasy layer paraphrasing them would be the story quietly taking over
 * the teaching.
 */
export function trickFor(index: GhostIndex, misconceptionId: string): Misconception | undefined {
  return index.misconceptionById.get(misconceptionId);
}

/* -------------------------------------------------------------------------- */
/* The seal                                                                   */
/* -------------------------------------------------------------------------- */

/** How many statements a seal offers. Four is enough to mean something. */
export const SEAL_CHOICES = 4;

export type SealChoice = {
  /** The misconception this statement describes. */
  misconceptionId: string;
  /** The catalog's blameless account of the error. What the student reads. */
  text: string;
  correct: boolean;
};

/**
 * The question that closes an encounter: *which trick was this?*
 *
 * `wardConceptId` is what the student is offered afterwards either way — a seal
 * is not a second grading, and getting it wrong shows the right statement and
 * seals anyway. Naming an error is metacognition, not mathematics, and running
 * it through mastery would be the same category error as counting a typo as a
 * wrong answer.
 */
export type Seal = {
  /** The trick actually played, and the one correct choice. */
  misconceptionId: string;
  ghostId: string;
  wardConceptId: string;
  choices: SealChoice[];
};

/**
 * Builds the seal for one trick.
 *
 * **Where the distractors come from, and why it is the only defensible rule
 * available.** A distractor must be wrong *and* on-topic. Drawing from another
 * strand would give the game away without a word being read — three statements
 * about triangles and one about fractions is a shape-matching puzzle — so the
 * pool is the *same kingdom*, which is on-topic by construction and is also the
 * right fiction: the other things that haunt these lands.
 *
 * Wrongness is where the care goes, and it is the grader's own rule one level
 * up: **a trap must never equal the correct answer.** Two misconceptions that
 * share a ward concept are two accounts of the same error — 'add-denominators'
 * and 'cross-multiply-to-add' are both repaired by Common Denominators — and
 * offering one as a distractor for the other would be telling a student their
 * correct self-diagnosis was wrong. So the pool excludes the answer's whole
 * ward, and it is deduplicated *by* ward, which makes the four statements four
 * genuinely different errors rather than four phrasings of two.
 *
 * That leaves at least nine candidate wards in the thinnest kingdom the world
 * has, which is why `validateGhosts` can prove every one of the 550 catalog
 * entries gets a full seal rather than hoping for it.
 *
 * Returns `undefined` for a misconception with no home or too small a pool.
 * The caller's honest move then is to skip the beat: a seal short of choices is
 * a question with the answer showing.
 */
export function sealFor(
  index: GhostIndex,
  misconceptionId: string,
  seed: string,
  count = SEAL_CHOICES,
): Seal | undefined {
  const answer = index.misconceptionById.get(misconceptionId);
  const ghost = index.byTrick.get(misconceptionId);
  if (!answer || !ghost || count < 2) return undefined;

  const seen = new Set([answer.remediationConceptId]);
  const texts = new Set([answer.description]);
  const pool: Misconception[] = [];

  for (const candidate of sortedByHash(kingdomPool(index, ghost, answer), seed)) {
    if (seen.has(candidate.remediationConceptId)) continue;
    // Belt and braces on the rule above: two catalog entries that happen to be
    // worded identically would be two correct answers on the same screen.
    if (texts.has(candidate.description)) continue;
    seen.add(candidate.remediationConceptId);
    texts.add(candidate.description);
    pool.push(candidate);
    if (pool.length === count - 1) break;
  }

  if (pool.length < count - 1) return undefined;

  const choices: SealChoice[] = [
    { misconceptionId: answer.id, text: answer.description, correct: true },
    ...pool.map((m) => ({ misconceptionId: m.id, text: m.description, correct: false })),
  ];

  return {
    misconceptionId: answer.id,
    ghostId: ghost.id,
    wardConceptId: ghost.wardConceptId,
    // Ordered, not shuffled: the correct statement must not sit first every
    // time, and it must sit in the same place on a reload.
    choices: sortedByHash(choices, `${seed}:order`, (choice) => choice.misconceptionId),
  };
}

/** Every catalog entry whose ghost haunts the same kingdom, minus the answer. */
function kingdomPool(index: GhostIndex, ghost: Ghost, answer: Misconception): Misconception[] {
  const pool: Misconception[] = [];
  for (const misconception of index.misconceptionById.values()) {
    if (misconception.id === answer.id) continue;
    if (index.byTrick.get(misconception.id)?.kingdomId !== ghost.kingdomId) continue;
    pool.push(misconception);
  }
  return pool;
}

/* -------------------------------------------------------------------------- */
/* What to ask about, and what has been met                                   */
/* -------------------------------------------------------------------------- */

/**
 * Which trick a seal should ask about, and whether the student actually took it.
 *
 * Two sources, in order.
 *
 * **What they did.** The commonest named misconception among their recent wrong
 * answers on this concept — read straight off the attempt log, exactly as
 * `journalEntries` reads it, so the two screens cannot describe the same
 * student differently. Ties break towards the most recent.
 *
 * Deliberately *not* windowed to the fight that just happened. A window would
 * have to come from a clock, and the only clock available is when the page
 * mounted — which moves on every reload, so a student who refreshed mid-fight
 * would be asked about a different ghost than the one that had been catching
 * them. The log's recent history is reload-proof, and the fight's own answers
 * are the newest entries in it anyway. The question the seal ends up asking is
 * "what has been catching you here", which is the better question.
 *
 * **What they were offered.** A clean run has no mistake to name, so the seal
 * falls back to a trap the exercises *carried* — real authored data
 * (`Exercise.traps`), never a consolation prize. "It laid this snare three
 * times and you never took it" is true and worth saying.
 *
 * `offeredTrickIds` are in the order the fight asked them; the first with a
 * ghost wins, so this stays a pure function with nothing rolled.
 *
 * `undefined` when there is nothing honest to ask — no mistake, no trap. The
 * caller's move then is to skip the beat entirely rather than invent one.
 */
export function sealTarget(
  index: GhostIndex,
  attempts: readonly AttemptRecord[],
  conceptId: string,
  offeredTrickIds: readonly string[] = [],
): { misconceptionId: string; taken: boolean } | undefined {
  const counts = new Map<string, { count: number; at: number }>();

  for (const attempt of attempts) {
    // `status`, not `correct`: malformed input is not a wrong answer, so it is
    // not a mistake anybody should be asked to name (`CLAUDE.md`).
    if (attempt.conceptId !== conceptId || attempt.status !== 'incorrect') continue;
    const trickId = attempt.misconceptionId;
    if (!trickId || !index.byTrick.has(trickId)) continue;
    const seen = counts.get(trickId);
    counts.set(trickId, { count: (seen?.count ?? 0) + 1, at: Math.max(seen?.at ?? 0, attempt.at) });
  }

  const commonest = [...counts.entries()].sort(
    (a, b) => b[1].count - a[1].count || b[1].at - a[1].at || a[0].localeCompare(b[0]),
  )[0];
  if (commonest) return { misconceptionId: commonest[0], taken: true };

  const offered = offeredTrickIds.find((trickId) => index.byTrick.has(trickId));
  return offered ? { misconceptionId: offered, taken: false } : undefined;
}

/**
 * One ghost the student has actually met, and how it has gone.
 *
 * "Met" means a wrong answer of one of its tricks inside the attempt log's
 * window. So the bestiary is a book of the student's *own* mistakes, and a
 * concept they have never fumbled has no page — which is why it can be shown
 * to them without reading as a list of everything they might get wrong.
 */
export type BestiaryEntry = {
  ghost: Ghost;
  /** Wrong answers of this ghost's tricks, inside the log's window. */
  encounters: number;
  /** The most recent of them, epoch milliseconds. The sort key. */
  at: number;
  /** Tricks of this ghost the student has met at all. */
  metTrickIds: string[];
  /** Of those, the ones they have correctly named. */
  namedTrickIds: string[];
  /** Every trick they have met is named. The tick on the page. */
  sealed: boolean;
};

/**
 * The student's bestiary: what has haunted them lately, most recent first.
 *
 * Derived, like everything else here and like `journalEntries` — the stored
 * half is only *which tricks have been named*, because that is the one fact the
 * attempt log cannot produce.
 *
 * **`encounters` is a count within the log's window** (`ATTEMPT_LOG_LIMIT`, 400
 * records), not since the student began. A ghost that has not come up in a long
 * time quietly stops having a page, which is the honest behaviour for a book of
 * what is going wrong lately, and is why the page is worded that way.
 *
 * Like the journal, this is the learner's own book (`CLAUDE.md` §14, §29):
 * nothing a teacher, a parent or a class surface reads may be built on it.
 */
export function bestiaryEntries(
  index: GhostIndex,
  attempts: readonly AttemptRecord[],
  namedTrickIds: readonly string[],
): BestiaryEntry[] {
  const named = new Set(namedTrickIds);
  const met = new Map<string, { ghost: Ghost; tricks: Map<string, number>; at: number; encounters: number }>();

  for (const attempt of attempts) {
    if (attempt.status !== 'incorrect') continue;
    const trickId = attempt.misconceptionId;
    const ghost = trickId ? index.byTrick.get(trickId) : undefined;
    if (!trickId || !ghost) continue;

    const entry = met.get(ghost.id) ?? { ghost, tricks: new Map<string, number>(), at: 0, encounters: 0 };
    entry.tricks.set(trickId, (entry.tricks.get(trickId) ?? 0) + 1);
    entry.encounters += 1;
    entry.at = Math.max(entry.at, attempt.at);
    met.set(ghost.id, entry);
  }

  return [...met.values()]
    .map((entry) => {
      const metTrickIds = [...entry.tricks.keys()].sort();
      const namedHere = metTrickIds.filter((trickId) => named.has(trickId));
      return {
        ghost: entry.ghost,
        encounters: entry.encounters,
        at: entry.at,
        metTrickIds,
        namedTrickIds: namedHere,
        sealed: namedHere.length === metTrickIds.length,
      };
    })
    .sort((a, b) => b.at - a.at || a.ghost.name.localeCompare(b.ghost.name));
}

/**
 * A ghost currently working on one *concept*.
 *
 * The bestiary's other axis. `bestiaryEntries` groups by creature — what keeps
 * recurring, wherever it catches you — and this groups by *place*: which
 * concept a ghost is standing on, so a region map can draw it. Two views of one
 * attempt log, and neither is derivable from the other, which is why both exist.
 */
export type Haunting = {
  conceptId: string;
  /** The creature that has caught them most often here. */
  ghost: Ghost;
  /** Wrong answers on this concept matching that ghost's tricks, in the window. */
  encounters: number;
  /** The most recent of them. */
  at: number;
  /** Every trick of this ghost met *here* has been named. */
  sealed: boolean;
};

/**
 * Where the student's ghosts are standing right now, by concept.
 *
 * This is what turns the map into a diagnostic. A region a student is fluent in
 * is quiet; a region they are struggling in is visibly haunted, on the tiles, in
 * the place the mathematics actually is — which is a thing a child will read
 * voluntarily, and more than can be said for a mastery bar.
 *
 * **A haunting is never a lock.** It is drawn, counted and named, and it stops
 * there: nothing in this file or above it may read it to decide whether a
 * lesson, a site or a region may be opened. A ghost is a *reason* to go
 * somewhere, and the moment it becomes a reason you may not, the seal invariant
 * has been broken one scale down from where it was written.
 *
 * Bounded by the attempt log's window like everything else derived from it, so
 * a concept that stopped going wrong quietly stops being haunted — which is the
 * honest behaviour, and the one that lets the map get quieter as a student
 * improves.
 */
export function hauntings(
  index: GhostIndex,
  attempts: readonly AttemptRecord[],
  namedTrickIds: readonly string[],
): Map<string, Haunting> {
  const named = new Set(namedTrickIds);

  /* (concept, ghost) → what has happened there. */
  const cells = new Map<string, { conceptId: string; ghost: Ghost; tricks: Set<string>; count: number; at: number }>();

  for (const attempt of attempts) {
    if (attempt.status !== 'incorrect') continue;
    const trickId = attempt.misconceptionId;
    const ghost = trickId ? index.byTrick.get(trickId) : undefined;
    if (!trickId || !ghost) continue;

    const key = `${attempt.conceptId} ${ghost.id}`;
    const cell = cells.get(key) ?? {
      conceptId: attempt.conceptId,
      ghost,
      tricks: new Set<string>(),
      count: 0,
      at: 0,
    };
    cell.tricks.add(trickId);
    cell.count += 1;
    cell.at = Math.max(cell.at, attempt.at);
    cells.set(key, cell);
  }

  const byConcept = new Map<string, Haunting>();
  for (const cell of cells.values()) {
    const standing = byConcept.get(cell.conceptId);
    // One ghost per concept: the commonest, ties to the most recent. A tile is
    // too small to hold two, and "what is catching you here" has one answer.
    if (standing && (standing.encounters > cell.count || (standing.encounters === cell.count && standing.at >= cell.at))) {
      continue;
    }
    byConcept.set(cell.conceptId, {
      conceptId: cell.conceptId,
      ghost: cell.ghost,
      encounters: cell.count,
      at: cell.at,
      sealed: [...cell.tricks].every((trickId) => named.has(trickId)),
    });
  }

  return byConcept;
}

/**
 * A deterministic order: hash the seed with each item's key, ties broken by the
 * key itself so the result cannot depend on the order the input arrived in.
 *
 * The one hashing scheme, in `@yuan-math/math`, shared with the practice page,
 * the diagnostic and every fight in the world.
 */
function sortedByHash<T>(items: readonly T[], seed: string, key: (item: T) => string = defaultKey): T[] {
  return [...items]
    .map((item) => ({ item, k: key(item), h: hashString(`${seed}:${key(item)}`) }))
    .sort((a, b) => a.h - b.h || a.k.localeCompare(b.k))
    .map((entry) => entry.item);
}

const defaultKey = (item: unknown): string => (item as Misconception).id;
